import { mq } from "@/constants/breakpoints";
import { darkColor, darkGreenColor, lightBlueColor } from "@/constants/colors";
import { gotham } from "@/utils/fonts";
import { css } from "@emotion/react";
import { useMemo } from "react";
import HR from "../HR/HR";
import { H3Css, H6Css, H8_10Css, H9Css, Heading } from "../Typography";
import PricingAnnualSvg from "./PricingAnnualSvg";
import Link from "next/link";
import { signUpPath } from "@/constants/paths";
import SpecialOfferBadge from "./SpecialOfferBadge";
import { useFeatureFlagVariantKey } from "posthog-js/react";

interface PricingCardProps {
  type: "starter" | "plus" | "pro";
  isAnnual: boolean;
}

const PricingCard = ({ type, isAnnual }: PricingCardProps) => {
  const introVariant = useFeatureFlagVariantKey("19-intro");

  const { label, price, texts, emails, saveAmount } = useMemo(() => {
    switch (type) {
      case "starter":
        return {
          label: "Starter",
          price: isAnnual ? "29" : "44",
          texts: "500",
          emails: "500",
          saveAmount: "180",
        };
      case "plus":
        return {
          label: "Plus",
          price: isAnnual ? "49" : "74",
          texts: "2,500",
          emails: "5,000",
          saveAmount: "300",
        };
      case "pro":
        return {
          label: "Pro",
          price: isAnnual ? "89" : "124",
          texts: "10,000",
          emails: "20,000",
          saveAmount: "420",
        };
    }
  }, [type, isAnnual]);

  return (
    <div
      css={css`
        border-radius: 12px;
        padding: 40px 30px;
        text-align: center;
        box-shadow: 0px 8px 24px rgba(116, 134, 196, 0.2);
        background-color: #fff;
        max-width: 370px;
        margin: auto;
        position: relative;
        ${mq["lg"]} {
          margin: unset;
        }
      `}
    >
      {introVariant === "test" && (
        <div
          css={css`
            position: absolute;
            top: -36px;
            left: -8px;
            width: 127px;
            height: 127px;
            ${mq["lg"]} {
              top: -53px;
              left: -20px;
              width: 143px;
              height: 143px;
            }
          `}
        >
          <SpecialOfferBadge />
        </div>
      )}

      {isAnnual && introVariant !== "test" && (
        <>
          <PricingAnnualSvg
            css={css`
              position: absolute;
              top: 6px;
              right: 6px;
            `}
          />
          <div
            css={css`
              position: absolute;
              top: 16px;
              right: 16px;
              color: #fff;
              text-align: right;
            `}
          >
            <Heading
              css={css`
                ${H8_10Css}
              `}
            >
              Save
            </Heading>
            <Heading
              css={css`
                margin-top: 3px;
                ${H6Css}
              `}
            >
              ${saveAmount}
            </Heading>
            <Heading
              css={css`
                ${H8_10Css};
              `}
            >
              / year
            </Heading>
          </div>
        </>
      )}

      <Heading
        level={5}
        css={css`
          color: ${darkColor};
          margin-bottom: 8px;
          ${H6Css}
        `}
      >
        {label}
      </Heading>
      <div
        css={css`
          display: flex;
          width: fit-content;
          margin: auto;
        `}
      >
        <Heading
          level={6}
          css={css`
            font-size: 18px;
            font-weight: 350;
            line-height: 22px;
            margin-top: 8px;
            color: ${darkColor};
          `}
        >
          $
        </Heading>
        <Heading
          level={6}
          css={css`
            color: ${darkColor};
            ${H3Css}
          `}
        >
          {price}
        </Heading>
      </div>
      <Heading
        css={css`
          color: ${darkGreenColor};
          ${H9Css};
        `}
      >
        / month
      </Heading>
      <Heading
        css={css`
          color: ${darkGreenColor};
          margin-top: 9px;
          font-weight: 350;
          font-size: 10px;
          line-height: 12px;
          visibility: ${isAnnual ? "visible" : "hidden"};
        `}
      >
        When Paid Annually
      </Heading>
      <div
        css={css`
          margin-top: 9px;
        `}
      >
        <HR />
      </div>

      <ul
        css={css`
          text-align: left;
          margin-left: 30px;
          list-style-type: none;
          li {
            margin-top: 26px;
            color: ${darkColor};
            background: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 12L10.75 15L17 9' stroke='%2355D278' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A")
              no-repeat top left;
            padding: 0 0 0 34px;
            vertical-align: middle;
            ${gotham.style};
            ${H9Css};
          }
        `}
      >
        <li
          css={css`
            color: ${lightBlueColor} !important;
          `}
        >
          Website with custom design
        </li>
        <li>Register players & collect payments online</li>
        <li>100% ad free website</li>
        <li>Includes free domain (your-organization.com)</li>
        <li>{texts} text messages / month</li>
        <li>{emails} emails / month</li>
      </ul>

      <div
        css={css`
          margin-top: 30px;
        `}
      >
        <Link
          href={signUpPath}
          css={css`
            text-decoration: none;
            display: block;
          `}
        >
          <button
            css={css`
              width: 100%;
              background-color: ${lightBlueColor};
              color: white;
              border: none;
              border-radius: 4px;
              padding: 14px 0;
              font-size: 16px;
              font-weight: 600;
              cursor: pointer;
              transition: background-color 0.2s;

              &:hover {
                background-color: #0080dd;
              }
            `}
          >
            Try For Free
          </button>
        </Link>
      </div>
    </div>
  );
};

export default PricingCard;
